import React from 'react';
import MaterialTable from '../DataTable/DataTable';
import { Button } from '@material-ui/core';
import { CSVLink } from 'react-csv';
import apiClient from '../../apiClient';
import withFlashNotification from '../../Pages/Notification/withFlashNotification';
import { notifyApiError } from '../../Pages/Notification/notificationHelpers';

const pageStyle = { maxWidth: '100%', padding: '50px' };
const errorPanelStyle = {
  alignItems: 'center', backgroundColor: '#ffebee', border: '1px solid #ef9a9a',
  color: '#b71c1c', display: 'flex', justifyContent: 'space-between',
  marginBottom: '16px', padding: '12px 16px',
};
const defaultTableOptions = {
  emptyRowsWhenPaging: false, filtering: true, pageSize: 10,
  pageSizeOptions: [5, 10, 25, 50], paging: true, search: true, sorting: true,
};

function queryFilters(query) {
  return (query.filters || [])
    .map((filter) => ({
      field: filter.column && filter.column.field,
      value: filter.value,
    }))
    .filter((filter) => filter.field && filter.value !== '' && filter.value != null);
}

class SharedDetails extends React.Component {
  state = {
    rows: [], loading: true, errorMessage: '', exportRows: [], exportLoading: false,
  };

  tableRef = React.createRef();
  csvExportRef = React.createRef();
  lastQuery = { page: 0, pageSize: 10, search: '', filters: [] };
  requestId = 0;

  componentDidMount() {
    if (!this.props.pagedEndpoint) this.loadRows();
  }

  componentDidUpdate(previousProps) {
    const remoteChanged = previousProps.pagedEndpoint !== this.props.pagedEndpoint ||
      previousProps.reloadKey !== this.props.reloadKey;
    if (this.props.pagedEndpoint && remoteChanged) {
      if (this.tableRef.current) this.tableRef.current.onQueryChange();
      return;
    }
    if (!this.props.pagedEndpoint && previousProps.endpoint !== this.props.endpoint) {
      this.loadRows();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.requestId += 1;
  }

  transformRows = (rows) => {
    const transform = this.props.transformRows || ((value) => value);
    return transform(Array.isArray(rows) ? rows : []);
  };

  loadRows = async () => {
    const requestId = this.requestId + 1;
    this.requestId = requestId;
    this.setState({ loading: true, errorMessage: '' });
    try {
      const response = await apiClient.get(this.props.endpoint);
      if (!Array.isArray(response.data)) throw new Error('Unexpected details list response');
      if (!this.unmounted && requestId === this.requestId) {
        this.setState({ rows: this.transformRows(response.data), loading: false });
      }
    } catch (error) {
      if (!this.unmounted && requestId === this.requestId) {
        const message = notifyApiError(
          this.props.showFlashNotification, error, this.props.errorMessage
        );
        this.setState({ rows: [], loading: false, errorMessage: message });
      }
    }
  };

  remoteParams = (query, page = query.page, pageSize = query.pageSize) => {
    const params = { page, pageSize, search: String(query.search || '').trim() };
    const filters = queryFilters(query);
    if (filters.length) params.filters = JSON.stringify(filters);
    if (query.orderBy && query.orderBy.field) {
      params.sortBy = query.orderBy.field;
      params.sortDir = query.orderDirection === 'desc' ? 'desc' : 'asc';
    }
    return params;
  };

  fetchRemotePage = async (query, page = query.page, pageSize = query.pageSize) => {
    const response = await apiClient.get(this.props.pagedEndpoint, {
      params: this.remoteParams(query, page, pageSize),
    });
    const payload = response.data || {};
    const sourceRows = payload[this.props.responseDataKey];
    if (!Array.isArray(sourceRows) || !Number.isFinite(Number(payload.totalCount))) {
      throw new Error('Unexpected paged details response');
    }
    return { rows: this.transformRows(sourceRows), totalCount: Number(payload.totalCount) };
  };

  loadRemoteRows = async (query) => {
    this.lastQuery = query;
    this.setState({ errorMessage: '' });
    try {
      const result = await this.fetchRemotePage(query);
      return { data: result.rows, page: query.page, totalCount: result.totalCount };
    } catch (error) {
      if (!this.unmounted) {
        const message = notifyApiError(
          this.props.showFlashNotification, error, this.props.errorMessage
        );
        this.setState({ errorMessage: message });
      }
      return { data: [], page: 0, totalCount: 0 };
    }
  };

  retry = () => {
    if (this.props.pagedEndpoint && this.tableRef.current) {
      this.tableRef.current.onQueryChange();
    } else {
      this.loadRows();
    }
  };

  exportRemoteRows = async () => {
    this.setState({ exportLoading: true, errorMessage: '' });
    try {
      const query = this.lastQuery;
      const first = await this.fetchRemotePage(query, 0, 100);
      const allRows = [...first.rows];
      const pageCount = Math.ceil(first.totalCount / 100);
      for (let page = 1; page < pageCount; page += 1) {
        const next = await this.fetchRemotePage(query, page, 100);
        allRows.push(...next.rows);
      }
      if (!allRows.length) throw new Error(this.props.emptyMessage);
      if (!this.unmounted) {
        this.setState({ exportRows: allRows, exportLoading: false }, () => {
          const csv = this.csvExportRef.current;
          if (csv && csv.link) csv.link.click();
          setTimeout(() => {
            if (!this.unmounted) this.setState({ exportRows: [] });
          }, 300);
        });
      }
    } catch (error) {
      if (!this.unmounted) {
        const message = notifyApiError(
          this.props.showFlashNotification, error, this.props.exportErrorMessage
        );
        this.setState({ exportLoading: false, errorMessage: message });
      }
    }
  };

  renderExport() {
    const { csvFilename, csvHeaders, exportLabel, pagedEndpoint } = this.props;
    const { exportLoading, exportRows, loading, rows } = this.state;
    if (pagedEndpoint) {
      return (
        <React.Fragment>
          <CSVLink
            data={exportRows} filename={csvFilename} headers={csvHeaders}
            ref={this.csvExportRef} style={{ display: 'none' }} target="_blank"
          >Export</CSVLink>
          <Button
            color="primary" disabled={exportLoading} onClick={this.exportRemoteRows}
            size="small" variant="outlined"
          >
            {exportLoading ? 'Preparing export…' : exportLabel}
          </Button>
        </React.Fragment>
      );
    }
    if (loading || rows.length === 0) {
      return <span aria-disabled="true" style={{ color: '#757575' }}>{exportLabel}</span>;
    }
    return (
      <CSVLink data={rows} filename={csvFilename} headers={csvHeaders} target="_blank">
        {exportLabel}
      </CSVLink>
    );
  }

  render() {
    const {
      actions, columns, editable, emptyMessage, loadingMessage, pagedEndpoint,
      style, tableOptions, title,
    } = this.props;
    const { errorMessage, loading, rows } = this.state;
    return (
      <div style={style || pageStyle}>
        {errorMessage ? (
          <div role="alert" style={errorPanelStyle}>
            <span>{errorMessage}</span>
            <Button color="secondary" onClick={this.retry} size="small" variant="outlined">
              Retry
            </Button>
          </div>
        ) : null}
        <div style={{ marginBottom: '12px', textAlign: 'right' }}>{this.renderExport()}</div>
        <MaterialTable
          actions={actions} columns={columns}
          data={pagedEndpoint ? this.loadRemoteRows : rows}
          editable={editable} isLoading={pagedEndpoint ? undefined : loading}
          tableRef={pagedEndpoint ? this.tableRef : undefined}
          title={title} options={{ ...defaultTableOptions, ...tableOptions }}
          localization={{
            body: {
              emptyDataSourceMessage: pagedEndpoint || !loading ? emptyMessage : loadingMessage,
            },
          }}
        />
      </div>
    );
  }
}

SharedDetails.defaultProps = {
  actions: undefined,
  editable: undefined,
  emptyMessage: 'No records found.',
  errorMessage: 'Unable to load records. Please try again.',
  exportErrorMessage: 'Unable to export records. Please try again.',
  exportLabel: 'Export to Excel',
  loadingMessage: 'Loading records...',
  reloadKey: 0,
  responseDataKey: 'rows',
  style: undefined,
  tableOptions: {},
  transformRows: undefined,
};

export { SharedDetails, defaultTableOptions, queryFilters };
export default withFlashNotification(SharedDetails);
